import {h} from 'preact';
import {useEffect, useContext, createContext, useState, useCallback} from "preact/compat";
import {useCookies} from "../../hooks";
import {api} from "../../services";
import {useAppContext, useGtmContext} from "../index";
import {parseLocationData} from "./userContext.helper";

// Creates a new empty context
const UserContext = createContext(null);

// This is the main app context in which we will store data such as session, location, login data, etc.
export const UserContextProvider = props => {
    // Destructured props
    const {children} = props;

    // The initial, empty, user data
    const [initialUserData] = useState({
        firstName:      "",
        customerId:     "",
        customerEmail:  "",
        sessionFormKey: ""
    });

    // The initial, empty, cart data
    const [initialCartData] = useState({
        cartId:   null,
        qtyItems: 0
    });

    // State to store all user data
    const [userData, setUserData] = useState(initialUserData);
    // State to validate whether the user is logged in or not
    const [isSignedIn, setIsSignedIn] = useCookies("user_signedIn", false);
    // State to store all cart data
    const [cartData, setCartData] = useState(initialCartData);
    // State to store all the rest, general data
    const [generalData, setGeneralData] = useState();

    // State to store term use status
    const [acceptedTermUse, setAcceptedTermUse] = useCookies('term_use', false);

    // Retrieves the methods to trigger the full page loader component and to set the available store locations
    const {
        setFullPageLoader,
        setLocations,
        setRecaptchaData,
        setSocialLoginConfig,
        setNoticesData
    } = useAppContext();

    const {addEventToGTM} = useGtmContext();

    // Method to sign the user out
    const signOut = async () => {
        try {
            // Activates the full page loader component
            setFullPageLoader(true);

            // Tries to perform the request to the backend
            let response = await api.get("/v1/login/logout");

            // Deactivates the full page loader component
            setFullPageLoader(false);

            // If logout is successful, then it redirects to the home page
            if (response.status === 200) {
                window.location.replace("/");
            }
        } catch (err) {
            console.error("Error while signing user out:", err);

            // Deactivates the full page loader component
            setFullPageLoader(false);
        }
    }

    // Fetches the initial user data when the page is loaded for the first time
    const fetchData = useCallback(async () => {
        // if (process.env.NODE_ENV !== 'development') {
        if (true) { // TODO: check the need for the above conditions
            try {
                // Performs the request to the backend
                let response = await api.get("/v1/data");

                // Validates whether the user is signed in or not by its name
                if (!response.data.session["customerName"]) {
                    setIsSignedIn(false);
                } else {
                    // Destructures the customer name prop from the session object
                    const {
                        customerEmail,
                        customerId,
                        customerName,
                        customer
                    } = response.data.session;

                    // Assigns the user data to the userData state
                    setUserData({
                        firstName: customerName,
                        customerId,
                        customerEmail,
                        customer
                    })

                    // Flags the user as signed in through it's name
                    setIsSignedIn(!!customerName);
                }

                // Adds the form key to the userData state
                setUserData(prevState => {
                    return {
                        ...prevState,
                        sessionFormKey: response.data.session.formKey
                    }
                })

                // Temporary array in which all parsed location data will be stored in
                const tempLocationsArray = [];

                // Iterates through each store to parse it
                response.data["stores"].forEach((item) => {
                    tempLocationsArray.push(parseLocationData(item));
                })

                // Sets the available store locations to the locations state in the app context
                setLocations(tempLocationsArray);

                // Sets the rest of the data to the generalData state
                setGeneralData({
                    ...response.data["data"],
                    affiliationData: {
                        vendor:     response?.data?.affiliation?.vendor,
                        client:     response?.data?.affiliation?.client,
                        dependents: response?.data?.affiliation?.dependents ? response?.data?.affiliation?.dependents : [],
                    }
                })

                // Sets the social login feature config data
                setSocialLoginConfig(response.data["socialLogin"]);

                // Adds the mini cart data to the cardData state
                const {miniCart} = response.data;

                // Sets the recaptcha data response into the recaptchaData state located at the app context
                setRecaptchaData(response.data.recaptcha);

                setCartData(miniCart);

                setNoticesData(response?.data?.notices);
            } catch (err) {
                console.error("Error while fetching user data:", err);
            }
        } else {
            const customerName = "Develop Test"

            setUserData({
                firstName:      customerName,
                customerId:     1,
                customerEmail:  "email@test.com",
                sessionFormKey: "VAPGJ3yl6Rye6W03",
                customer: {
                    shippingAddress: {
                        isPending:        false,
                        data: {
                            city:         null,
                            address:      "",
                            number:       null,
                            neighborhood: null,
                            complement:   null,
                            phone:        null,
                            taxVat:       null,
                            region:       null,
                            country:      null
                        }
                    }
                }
            })

            setIsSignedIn(!!customerName);

            // setUserData(prevState => ({
            //     ...prevState,
            //     sessionFormKey: "VAPGJ3yl6Rye6W03"
            // }))

            setLocations([
                {
                    id:   1,
                    code: "default",
                    name: "Default Store View"
                }
            ]);

            setGeneralData({
                desktopNewsletterModal:  true,
                mobileNewsletterModal:   true,
                desktopNewsletterFooter: true,
                mobileNewsletterFooter:  true,
                nadEnable:               true,
                affiliationData: {
                    vendor:     "RITA",
                    client: {
                        nome:   "Renato Ferreira",
                        cpf:    "23416739884",
                        email:  "heloisa93@example.org",
                        uf:     "DF",
                        cidade: "Brasília",
                        tabela: "Tabela Especial"
                    },
                    dependents: null
                }
            });

            setRecaptchaData({
                isEnabled: true,
                token:     "6LeYxwcpAAAAAFfyH1v9QeU7WfSPbEhy1wZW4Y1K"
            });

            setCartData({
                cartId:   null,
                qtyItems: 0,
                nadLock:  false
            });

            setSocialLoginConfig({
                isGeneralEnabled:        false,
                isAccountLinkingEnabled: false,
                isGoogleEnabled:         false,
                isFacebookEnabled:       false
            })
        }
    }, [setIsSignedIn]);

    // Effect to trigger the client_id GTM event
    useEffect(() => {
        // Prevents instructions from running if the user isn't logged in
        if (!isSignedIn) {
            return;
        }

        // Event data
        const clientDetails = {
            event:     'client_id',
            client_id: {
                id: userData.customerId
            }
        }

        addEventToGTM(clientDetails);
    }, [isSignedIn]);

    // Effect that triggers the execution of the fetch operation
    useEffect(() => {
        fetchData().then(() => {});
    }, []);

    // The data that will be soon returned by the context
    const data = [
        {
            ...userData,
            isSignedIn,
            setIsSignedIn
        },
        {
            signOut
        },
        {
            cartData,
            setCartData,
            acceptedTermUse,
            setAcceptedTermUse
        },
        {
            ...generalData
        }
    ];

    return (
        <UserContext.Provider value={data}>
            {children}
        </UserContext.Provider>
    )
}

// Hook to provide a simple context accessibility
const useUserContext = () => useContext(UserContext);
export default useUserContext;
